import { useCallback, useEffect, useRef, useState } from 'react';
import type { Evento, Pedido } from '../../api/ops/pedidos';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { dinero } from '../../logica/dinero';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { enlaceDeReferencia } from '../../logica/navegacion';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import {
    CANAL,
    ESTADO_PEDIDO,
    FORMA_PAGO,
    PERMISOS,
    avisoDeEstado,
    cuandoEvento,
    entradasDe,
    formasDeReembolso,
    importeDevuelto,
    lineasDe,
    nombreComprador,
    opsEntradas,
    pagoEnElLocal,
    textoCancelado,
    textoDeError,
    type FormaReembolso,
} from './textos';

/**
 * Ficha del pedido (10.6) en RV Panel, Resumen · Entradas · Pago:
 * - Resumen: evento, entradas, canal (con quién vendió y la tablet en taquilla, G786, o el código del RRPP), comprador
 *   (solo el nombre; G511 «Cliente suprimido»), cuándo, llegadas y total con los gastos de gestión aparte. «Ver la
 *   ficha» del cliente y «Reenviar las entradas» (`resendPass` de cada pase, 5 por pase y hora).
 * - Entradas: una por asistente con su llegada; «Corregir el nombre» en los nominativos (G508) con «Deshacer» 5 s.
 * - Pago: forma de pago, subtotal, gastos de gestión, total y lo devuelto (cifras de la API).
 * - «Cancelar y reembolsar» (Propietario y Administrador): por defecto sin gastos de gestión (Pagos, pregunta 4), en
 *   taquilla «En el local»; motivo y ventana «No se puede deshacer». «Reintentar» un reembolso fallido.
 */
interface Props extends PropsPantalla {
    id: string | null;
    nombres: Readonly<Record<string, string>>;
    eventos: ReadonlyMap<string, Evento>;
    onCerrar: () => void;
    onCambiado: () => void;
}

type Pestana = 'resumen' | 'entradas' | 'pago';

export function FichaPedido(props: Props) {
    const { negocio, movil, id, nombres, eventos, onCerrar, onCambiado, soloLectura, destinos = [], navegar, accesoPlataforma = false } = props;
    const ops = opsEntradas(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [leido, setLeido] = useState<{ pedido: Pedido; version: string | null } | null>(null);
    const [evento, setEvento] = useState<Evento | null>(null);
    const [nombreCliente, setNombreCliente] = useState<string | null>(null);
    const [pestana, setPestana] = useState<Pestana>('resumen');
    const [intento, setIntento] = useState(0);
    const [modo, setModo] = useState<'ficha' | 'reembolsar'>('ficha');
    const [ventana, setVentana] = useState<'reembolsar' | 'reintentar' | 'reenviar' | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [forma, setForma] = useState<FormaReembolso>('without_booking_fee');
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [editando, setEditando] = useState<{ ticket: string; valor: string; error: string | null } | null>(null);
    const [nombresNuevos, setNombresNuevos] = useState<Readonly<Record<string, string>>>({});

    const [idAntes, setIdAntes] = useState(id);
    if (id !== idAntes) {
        setIdAntes(id);
        setModo('ficha');
        setPestana('resumen');
        setEditando(null);
        setNombresNuevos({});
    }

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    const cerrar = useRef(onCerrar);
    useEffect(() => {
        cerrar.current = onCerrar;
    });

    useEffect(() => {
        if (id === null) {
            return;
        }
        let vivo = true;
        ops.getTicketOrder(negocio, id)
            .then((r) => {
                if (vivo) {
                    setLeido({ pedido: r.datos, version: r.version });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    cerrar.current();
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido abrir el pedido. Prueba de nuevo.') });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, intento, avisar]);

    const p = leido !== null && leido.pedido.id === id ? leido.pedido : null;
    const idEvento = p?.event_id ?? null;
    const conocido = idEvento === null ? undefined : eventos.get(idEvento);

    // El evento (nominativo o no) si no venía en la lista.
    useEffect(() => {
        if (idEvento === null || conocido !== undefined) {
            return;
        }
        let vivo = true;
        ops.getEvent(negocio, idEvento)
            .then((r) => {
                if (vivo) {
                    setEvento(r.datos);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, idEvento, conocido]);

    // El nombre del comprador si la lista no lo tenía (al abrir desde un aviso).
    const idCliente = p?.customer_id ?? null;
    const sabido = idCliente === null ? undefined : nombres[idCliente];
    const verClientes = permisos.puede(PERMISOS.clientes);
    useEffect(() => {
        if (idCliente === null || sabido !== undefined || !verClientes) {
            return;
        }
        let vivo = true;
        ops.listCustomersByIds(negocio, [idCliente])
            .then((l) => {
                if (vivo) {
                    setNombreCliente(l.data[0]?.display_name ?? '');
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, idCliente, sabido, verClientes]);

    const ev = conocido ?? (evento !== null && evento.id === idEvento ? evento : undefined);
    const nombre = p === null ? '' : nombreComprador(p, idCliente !== null && sabido === undefined && nombreCliente !== null ? { ...nombres, [idCliente]: nombreCliente } : nombres);
    const sinPlataforma = !accesoPlataforma;
    const puedeReembolsar = p !== null && sinPlataforma && !soloLectura && permisos.puede(PERMISOS.reembolsar) && p.status === 'confirmed' && p.payment_disputed !== true;
    const puedeReintentar = p !== null && sinPlataforma && !soloLectura && permisos.puede(PERMISOS.reembolsar) && p.refund_status === 'failed';
    const pases = (p?.tickets ?? []).filter((t) => t.status === 'valid' && t.pass_id != null);
    const puedeReenviar = p !== null && !soloLectura && permisos.puede(PERMISOS.reenviar) && p.status === 'confirmed' && p.customer_id != null && pases.length > 0;
    const puedeCorregir = p !== null && !soloLectura && permisos.puede(PERMISOS.gestionar) && p.status === 'confirmed' && ev?.nominative === true;
    const enlaceCliente = idCliente === null || !verClientes ? null : enlaceDeReferencia(destinos, 'cliente', idCliente);

    const hecho = (texto: string) => {
        setOcupado(false);
        setVentana(null);
        setModo('ficha');
        avisar({ tipo: 'ok', texto });
        recargar();
        onCambiado();
    };
    const fallo = (e: unknown, porDefecto: string) => {
        setOcupado(false);
        setVentana(null);
        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, porDefecto) });
        recargar();
    };

    // ---- Cancelar y reembolsar ----
    const formas = p === null ? [] : formasDeReembolso(p);
    const formaElegida: FormaReembolso = formas.includes(forma) ? forma : (formas[0] ?? 'full');
    const devuelves = p === null ? null : importeDevuelto(p, formaElegida);
    const errMotivo = intentado && motivo.trim().length < 10 ? 'Explica el motivo (mínimo 10 caracteres).' : null;
    const abrirReembolso = () => {
        setForma('without_booking_fee');
        setMotivo('');
        setIntentado(false);
        setModo('reembolsar');
    };
    const pedirConfirmacion = () => {
        setIntentado(true);
        if (motivo.trim().length < 10) {
            return;
        }
        setVentana('reembolsar');
    };
    const reembolsar = () => {
        if (p === null || leido === null) {
            return;
        }
        setOcupado(true);
        ops.cancelTicketOrder(negocio, p.id, { refund: formaElegida, reason: motivo.trim() }, leido.version ?? `"${String(p.version)}"`)
            .then((r) => {
                hecho(formaElegida === 'none_external' ? `Pedido ${r.reference} cancelado: el dinero se lo devuelves tú.` : `Pedido ${r.reference} cancelado: Stripe devuelve ${dinero(devuelves)}.`);
            })
            .catch((e: unknown) => {
                fallo(e, 'No he podido cancelar el pedido. Prueba de nuevo.');
            });
    };

    // ---- Reenviar ----
    const reenviar = () => {
        if (p === null) {
            return;
        }
        setOcupado(true);
        Promise.all(pases.map((t) => ops.resendPass(negocio, t.pass_id ?? '')))
            .then(() => {
                setOcupado(false);
                setVentana(null);
                avisar({ tipo: 'ok', texto: pases.length === 1 ? 'Entrada reenviada.' : 'Entradas reenviadas.' });
            })
            .catch((e: unknown) => {
                setOcupado(false);
                setVentana(null);
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido reenviarlas. Prueba de nuevo.') });
            });
    };

    // ---- Corregir el nombre (G508), con «Deshacer» 5 s ----
    const guardarNombre = () => {
        if (p === null || editando === null || leido === null) {
            return;
        }
        const valor = editando.valor.trim();
        if (valor.length < 2) {
            setEditando({ ...editando, error: 'Escribe el nombre.' });
            return;
        }
        const ticket = p.tickets.find((t) => t.id === editando.ticket);
        if (ticket === undefined) {
            return;
        }
        const version = leido.version ?? `"${String(p.version)}"`;
        setEditando(null);
        setNombresNuevos((x) => ({ ...x, [ticket.id]: valor }));
        envio.programar({
            texto: `Entrada ${String(ticket.seq)}: ahora a nombre de ${valor}. Su pase se actualiza en 5 segundos.`,
            donde: 'entradas',
            enviar: () => {
                ops.updateTicketHolder(negocio, p.id, ticket.id, valor, version)
                    .then(() => {
                        recargar();
                    })
                    .catch((e: unknown) => {
                        setNombresNuevos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== ticket.id)));
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido corregir el nombre. Prueba de nuevo.') });
                        recargar();
                    });
            },
            alDeshacer: () => {
                setNombresNuevos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== ticket.id)));
            },
        });
    };

    const [estadoTexto, estadoChip]: readonly [string, EstadoChip] = p === null ? ['', 'neutral'] : p.payment_disputed === true ? ['En disputa', 'problem'] : ESTADO_PEDIDO[p.status];
    const accion = modo === 'reembolsar' ? (formaElegida === 'none_external' ? 'Cancelar el pedido' : 'Reembolsar') : puedeReembolsar ? 'Cancelar y reembolsar' : puedeReintentar ? 'Reintentar el reembolso' : '';

    const cuerpo = (() => {
        if (p === null) {
            return <EstadoCargando seccion="el pedido" />;
        }
        if (modo === 'reembolsar') {
            return (
                <div className="rv-ca-cuerpo">
                    <div className="rv-ca-caja">
                        <div className="rv-ca-caja__fila">
                            <Icono nombre="ticket" />
                            <span>{`Se anulan sus ${String(pases.length || entradasDe(p))} pases y se liberan sus ${String(entradasDe(p))} plazas${ev?.name === undefined ? '' : ` de ${ev.name}`}.`}</span>
                        </div>
                    </div>
                    <span className="rv-ca-ceja">Cuánto devuelves</span>
                    <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`} role="radiogroup" aria-label="Cuánto devuelves">
                        {formas.map((f) =>
                            f === 'none_external' ? (
                                <OpcionRadio key={f} elegida titulo="En el local, fuera de Reservaria" texto={`Se pagó con ${FORMA_PAGO[p.payment_method][0].toLocaleLowerCase('es')}: el dinero se lo devuelves tú.`} onElegir={() => { setForma(f); }} />
                            ) : (
                                <OpcionRadio
                                    key={f}
                                    elegida={formaElegida === f}
                                    titulo={`${dinero(importeDevuelto(p, f))} · ${f === 'full' ? 'Todo' : 'Sin gastos de gestión'}`}
                                    texto={f === 'full' ? 'Con los gastos de gestión: si cancelas tú o hay un error vuestro.' : 'Lo habitual si cancela el comprador por su cuenta.'}
                                    onElegir={() => { setForma(f); }}
                                />
                            ),
                        )}
                    </div>
                    <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} filas={3} max={500} error={errMotivo} ayuda="Queda en Actividad." />
                    <p className="rv-ca-nota rv-ca-nota--gris">
                        <Icono nombre="info" />
                        <span>{formaElegida === 'none_external' ? 'Si el pago se anotó mal, anúlalo desde Pagos.' : 'Stripe lo devuelve a la tarjeta con la que pagó. Si el reembolso falla, te avisamos y podrás reintentarlo.'}</span>
                    </p>
                </div>
            );
        }
        const aviso = avisoDeEstado(p, new Date(), permisos.puede(PERMISOS.reembolsar), props.membresia?.timezone);
        return (
            <div className="rv-ca-cuerpo">
                {aviso !== null && (
                    <div className={`rv-ca-caja${aviso.error ? ' rv-ca-caja--error' : ' rv-ca-caja--aviso'}`} role={aviso.error ? 'alert' : 'status'}>
                        <div className="rv-ca-caja__fila"><Icono nombre={aviso.icono} /><span className="rv-ca-fila__texto"><strong>{aviso.titulo}</strong><span>{aviso.texto}</span></span></div>
                    </div>
                )}
                {envio.pendiente !== null && (
                    <div className="rv-ca-hecho" role="status">
                        <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                        <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                        <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                    </div>
                )}
                {pestana === 'resumen' && <Resumen p={p} ev={ev} nombre={nombre} />}
                {pestana === 'resumen' && (
                    <div className="rv-pe-botones">
                        {enlaceCliente !== null && (
                            <span><button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(enlaceCliente.ruta); }}><Icono nombre="user" /><span>Ver la ficha</span></button></span>
                        )}
                        {puedeReenviar && (
                            <>
                                <span><button type="button" className="rv-b rv-b-sec" onClick={() => { setVentana('reenviar'); }}><Icono nombre="send" /><span>Reenviar las entradas</span></button></span>
                                <p className="rv-ca-nota rv-ca-nota--gris"><span>Al contacto de su ficha, por su canal preferido y con respaldo por email. Nunca a otro contacto. Hasta 5 reenvíos por entrada y hora.</span></p>
                            </>
                        )}
                    </div>
                )}
                {pestana === 'entradas' && (
                    <div className="rv-ca-bloque" role="list" aria-label="Entradas del pedido">
                        {p.tickets.map((t) => {
                            const linea = p.lines.find((l) => l.ticket_type_id === t.ticket_type_id);
                            const valida = t.status === 'valid' && p.status === 'confirmed';
                            const chip: readonly [string, EstadoChip] = valida ? (t.arrived_at == null ? ['Válida', 'confirmed'] : ['Entró', 'confirmed']) : p.status === 'pending_payment' ? ['Sin pase', 'pending'] : ['Anulada', 'neutral'];
                            const titular = nombresNuevos[t.id] ?? t.holder_name ?? `Entrada ${String(t.seq)}`;
                            return (
                                <div key={t.id} role="listitem" className="rv-pe-entrada">
                                    <span className="rv-pe-entrada__fila">
                                        <span className="rv-ca-baldosa">{t.seq}</span>
                                        <span className="rv-ca-fila__texto">
                                            <strong>{titular}</strong>
                                            <span>{[linea?.name ?? 'Entrada', dinero(t.unit_price), valida ? 'pase emitido' : ''].filter((x) => x !== '').join(' · ')}</span>
                                        </span>
                                        <span className="rv-pe-entrada__estado">
                                            <Chip estado={chip[1]}>{chip[0]}</Chip>
                                            {valida && <small>{t.arrived_at == null ? 'Sin llegar' : `Entró a las ${hora(t.arrived_at)}`}</small>}
                                        </span>
                                    </span>
                                    {puedeCorregir && t.status === 'valid' && editando?.ticket !== t.id && (
                                        <span className="rv-pe-entrada__accion">
                                            <button type="button" className="rv-b rv-b-sec" onClick={() => { setEditando({ ticket: t.id, valor: titular.startsWith('Entrada ') ? '' : titular, error: null }); }}><Icono nombre="pencil" /><span>Corregir el nombre</span></button>
                                        </span>
                                    )}
                                    {editando?.ticket === t.id && (
                                        <span className="rv-pe-entrada__accion rv-pe-editar">
                                            <Campo etiqueta="Nombre del asistente" valor={editando.valor} onChange={(v) => { setEditando({ ...editando, valor: v, error: null }); }} onEnter={guardarNombre} error={editando.error} mayusculas="words" max={120} autoFocus ayuda="Cambia el nombre en su pase y en la cartera: el pase pasa a una versión nueva con el mismo QR." />
                                            <span className="rv-pe-botones-fila">
                                                <button type="button" className="rv-b rv-b-sec" onClick={() => { setEditando(null); }}><Icono nombre="x" /><span>Cancelar</span></button>
                                                <button type="button" className="rv-b rv-b-pri" onClick={guardarNombre}><Icono nombre="check" /><span>Guardar</span></button>
                                            </span>
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        <p className="rv-ca-nota rv-ca-nota--gris">
                            <Icono nombre="info" />
                            <span>{ev?.nominative === true ? 'Todas las entradas van al comprador, cada una con el nombre de su asistente. Corregir el nombre no cambia el QR.' : 'Una entrada por asistente, cada una con su pase. Las llegadas las registra la puerta.'}</span>
                        </p>
                    </div>
                )}
                {pestana === 'pago' && <Pago p={p} destinos={destinos} navegar={navegar} />}
            </div>
        );
    })();

    return (
        <>
            <PanelLateral
                abierto={id !== null}
                onCerrar={modo === 'reembolsar' ? () => { setModo('ficha'); } : onCerrar}
                movil={movil}
                tamano={modo === 'reembolsar' ? 'formulario' : 'ficha'}
                titulo={p === null ? 'Pedido' : modo === 'reembolsar' ? (formaElegida === 'none_external' ? 'Cancelar el pedido' : 'Cancelar y reembolsar') : nombre}
                eyebrow={p === null ? undefined : `Pedido ${p.reference}`}
                subtitulo={p === null ? undefined : modo === 'reembolsar' ? `${nombre} · ${lineasDe(p)}` : [ev?.name ?? '', cuandoEvento(ev)].filter((x) => x !== '').join(' · ')}
                chip={p === null || modo === 'reembolsar' ? undefined : estadoTexto}
                chipEstado={estadoChip}
                icono={modo === 'reembolsar' ? 'rotate-ccw' : 'receipt'}
                accion={accion}
                accionIcono={puedeReintentar && !puedeReembolsar && modo === 'ficha' ? 'refresh-cw' : 'rotate-ccw'}
                cerrar={modo === 'reembolsar' ? 'Volver' : 'Cerrar'}
                onAccion={modo === 'reembolsar' ? pedirConfirmacion : puedeReembolsar ? abrirReembolso : () => { setVentana('reintentar'); }}
            >
                {p !== null && modo === 'ficha' && (
                    <div className="rv-ca-botonera" role="tablist" aria-label="Partes del pedido">
                        {(
                            [
                                ['resumen', 'Resumen', 'receipt'],
                                ['entradas', 'Entradas', 'ticket'],
                                ['pago', 'Pago', 'credit-card'],
                            ] as const
                        ).map(([k, t, ic]) => (
                            <button key={k} type="button" role="tab" aria-selected={pestana === k} className="rv-ca-botonera__b" onClick={() => { setPestana(k); setEditando(null); }}>
                                <Icono nombre={ic} />
                                <span>{t}</span>
                            </button>
                        ))}
                    </div>
                )}
                {cuerpo}
            </PanelLateral>
            <Ventana
                abierta={ventana === 'reembolsar'}
                titulo={p === null ? '' : formaElegida === 'none_external' ? `Cancelar el pedido ${p.reference}` : `Cancelar y reembolsar ${dinero(devuelves)}`}
                texto={formaElegida === 'none_external' ? 'Sus pases dejan de valer en la puerta. El dinero se lo devuelves tú. No se puede deshacer.' : `Sus pases dejan de valer y Stripe devuelve el dinero a ${nombre}, que recibe un aviso. No se puede deshacer.`}
                accion={formaElegida === 'none_external' ? 'Cancelar el pedido' : 'Reembolsar'}
                accionIcono="rotate-ccw"
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto="Cancelando…"
                movil={movil}
                onAccion={reembolsar}
                onCerrar={() => { setVentana(null); }}
            >
                {p !== null && (
                    <div className="rv-ca-ventana-lineas">
                        <Linea k="Pedido" v={`${p.reference} · ${lineasDe(p)}`} />
                        {ev?.name !== undefined && <Linea k="Evento" v={ev.name} />}
                        <Linea k="Devuelves" v={devuelves === null ? 'En el local, fuera de Reservaria' : dinero(devuelves)} />
                        <Linea k="Motivo" v={motivo.trim()} />
                    </div>
                )}
            </Ventana>
            <Ventana
                abierta={ventana === 'reintentar'}
                titulo="¿Reintentar el reembolso?"
                texto={`Pedimos otra vez el reembolso a Stripe por la misma cantidad para ${nombre}. Si vuelve a fallar, te avisamos.`}
                accion="Reintentar"
                accionIcono="refresh-cw"
                ocupado={ocupado}
                ocupadoTexto="Pidiendo…"
                movil={movil}
                onAccion={() => {
                    if (p === null) {
                        return;
                    }
                    setOcupado(true);
                    ops.retryTicketOrderRefund(negocio, p.id)
                        .then(() => { hecho('Reembolso pedido otra vez.'); })
                        .catch((e: unknown) => { fallo(e, 'Stripe no ha aceptado el reembolso. Prueba más tarde.'); });
                }}
                onCerrar={() => { setVentana(null); }}
            />
            <Ventana
                abierta={ventana === 'reenviar'}
                titulo="¿Reenviar las entradas?"
                texto={`Le enviamos otra vez ${pases.length === 1 ? 'su entrada' : `sus ${String(pases.length)} entradas`} al contacto de su ficha, por su canal preferido y con respaldo por email.`}
                accion="Reenviar"
                accionIcono="send"
                ocupado={ocupado}
                ocupadoTexto="Reenviando…"
                movil={movil}
                onAccion={reenviar}
                onCerrar={() => { setVentana(null); }}
            >
                <div className="rv-ca-ventana-lineas">
                    <Linea k="A" v={nombre} />
                    <Linea k="Entradas" v={String(pases.length)} />
                </div>
            </Ventana>
        </>
    );
}

const hora = (iso: string) => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

function Resumen({ p, ev, nombre }: { p: Pedido; ev: Evento | undefined; nombre: string }) {
    const [canal, iconoCanal] = CANAL[p.channel];
    const llegadas = p.tickets.filter((t) => t.arrived_at != null).length;
    const n = entradasDe(p);
    const subCanal = (() => {
        if (p.channel === 'box_office' && (p.created_by_member_id != null || p.device_id != null)) {
            // G926: los nombres vienen guardados en el pedido; no se leen miembros ni tablets.
            const quien = p.created_by_name != null && p.created_by_name !== '' ? `Vendido por ${p.created_by_name}` : 'Vendido por alguien del equipo';
            const donde = p.device_id == null ? '' : p.device_name != null && p.device_name !== '' ? ` con la tablet ${p.device_name}` : ' con una tablet';
            return `${quien}${donde}${p.offline === true ? ' · sin conexión' : ''}`;
        }
        return p.promoter_ref == null ? undefined : `Con el enlace de venta del RRPP (código ${p.promoter_ref})`;
    })();
    const subComprador = p.cancelled_by === 'customer_privacy' ? 'Pidió borrar sus datos' : p.customer_id == null ? (p.channel === 'box_office' ? 'Sin datos: pagó en la puerta' : undefined) : 'Su contacto está en su ficha de cliente';
    return (
        <div className="rv-ca-bloque">
            <Dato k="Evento" icono="calendar-days" v={ev?.name ?? 'Evento'} sub={cuandoEvento(ev)} />
            <Dato k="Entradas" icono="ticket" v={lineasDe(p)} sub={ev?.nominative === true ? 'Nominativas: una por asistente, con su nombre' : undefined} />
            <Dato k="Canal" icono={iconoCanal} v={canal} sub={subCanal} />
            <Dato k="Comprador" icono="user" v={nombre} sub={subComprador} />
            <Dato k="Comprado" icono="clock" v={diaYHora(p.created_at)} />
            {p.status === 'confirmed' && <Dato k="Llegadas" icono="door-open" v={`${String(llegadas)} de ${String(n)}`} sub="Se actualiza con cada pase que entra" />}
            {(p.status === 'cancelled' || p.status === 'refunded') && <Dato k="Cancelado" icono="ban" v={textoCancelado(p)} sub={diaYHora(p.cancelled_at)} />}
            <Dato k="Total" icono="euro" v={dinero(p.total)} sub={p.channel === 'box_office' || p.booking_fee.amount === 0 ? undefined : `${dinero(p.booking_fee)} de gastos de gestión`} />
        </div>
    );
}

function Pago({ p, destinos, navegar }: { p: Pedido; destinos: NonNullable<PropsPantalla['destinos']>; navegar: (ruta: string) => void }) {
    const [forma, icono] = FORMA_PAGO[p.payment_method];
    const enlacePago = p.payment_id == null ? null : enlaceDeReferencia(destinos, 'pago', p.payment_id);
    const devuelto = p.refunded_amount !== undefined && p.refunded_amount.amount > 0;
    return (
        <div className="rv-ca-bloque">
            <Dato k="Forma de pago" icono={icono} v={forma} sub={pagoEnElLocal(p) ? 'Reservaria solo anotó la forma de pago' : p.payment_method === 'online' ? 'Con Stripe · el detalle está en Pagos' : undefined} />
            <Dato k="Entradas" icono="ticket" v={dinero(p.subtotal)} />
            <Dato k="Gastos de gestión" icono="percent" v={dinero(p.booking_fee)} sub={p.channel === 'box_office' ? 'En taquilla no hay gastos de gestión' : '1 € + 1,5 % por entrada, los paga el comprador'} />
            <Dato k="Total" icono="euro" v={dinero(p.total)} sub={p.status === 'pending_payment' ? 'Sin pagar todavía' : p.status === 'expired' ? 'No llegó a pagar' : undefined} />
            {devuelto && <Dato k="Reembolsado" icono="rotate-ccw" v={dinero(p.refunded_amount)} />}
            {p.refund_status === 'requested' && <Dato k="Reembolso" icono="rotate-ccw" v="En curso" />}
            {enlacePago !== null && (
                <span><button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(enlacePago.ruta); }}><Icono nombre="arrow-right" /><span>Ver el pago</span></button></span>
            )}
        </div>
    );
}

function Dato({ k, v, sub, icono }: { k: string; v: string; sub?: string; icono: NombreIcono }) {
    return (
        <div className="rv-ca-dato rv-pe-dato">
            <span className="rv-pe-dato__k"><Icono nombre={icono} />{k}</span>
            <span className="rv-pe-dato__v">
                <strong>{v}</strong>
                {sub !== undefined && sub !== '' && <small>{sub}</small>}
            </span>
        </div>
    );
}

function Linea({ k, v }: { k: string; v: string }) {
    return (
        <div className="rv-ca-ventana-linea">
            <span>{k}</span>
            <strong>{v}</strong>
        </div>
    );
}
